import type { BaseQueryFn, TypedMutationOnQueryStarted } from "@reduxjs/toolkit/query";

import {
  BusinessReportOutcome,
  BusinessReportOutcomeDetails,
  PromptDataType,
  TrafficLightRating,
} from "@/modules/snapshots/businessReports.types";
import { BUSINESS_REPORTS_TAGS } from "@/modules/snapshots/businessReports.constraints";
import { SNAPSHOT_COLLECTION_TAGS } from "@/modules/snapshots/snapshotCollection.constraints";
import type { SnapshotGridQueryPayload } from "@/modules/snapshots/grid/gridQueryPayload";
import { snapshotViewsApi } from "@/modules/snapshots/snapshotViews.api";
import {
  applyOutcomePatch,
  isInPlaceOutcomePatch,
  outcomePatchFromDetails,
  patchAddsUnknownNoteColumn,
  patchOutcomeInRows,
  replaceOutcomeInRows,
  storedResultId,
  type OutcomeCachePatch,
} from "./outcomeCachePatch";
import {
  ContractFamilyProjectionGridQueryResponse,
  SnapshotGridQueryResponse,
  SnapshotReviewRun,
  SnapshotReviewRunProgress,
} from "./types";
import { outcomesApi } from "./outcomes";

interface SnapshotCollectionFailedResultsRetryResponse {
  retried_results: number;
  affected_outcomes: number;
}

/**
 * Grid pages provide the outcome tag twice: bare, so every outcome-level mutation (create, delete,
 * retry, note columns, subcollection membership) still refetches them, and scoped to the collection,
 * so a cell edit can refresh one collection's grid without touching any other cached grid.
 */
const collectionGridTags = (snapshotCollectionId: string) => [
  BUSINESS_REPORTS_TAGS.BUSINESS_REPORTS_OUTCOME_TAG,
  { type: BUSINESS_REPORTS_TAGS.BUSINESS_REPORTS_OUTCOME_TAG, id: snapshotCollectionId },
];

/** Snapshot collection grid: rows, cell edits, retries, note/AI columns and review runs. */
export const gridApi = outcomesApi.injectEndpoints({
  overrideExisting: false,
  endpoints: builder => ({
    retrySnapshotCollectionFailedResults: builder.mutation<
      SnapshotCollectionFailedResultsRetryResponse,
      { snapshotCollectionId: string }
    >({
      invalidatesTags: [
        BUSINESS_REPORTS_TAGS.BUSINESS_REPORTS_OUTCOME_TAG,
        SNAPSHOT_COLLECTION_TAGS.SNAPSHOT_COLLECTION_TAG,
      ],
      query: ({ snapshotCollectionId }) => ({
        url: `/pf-mainweb/business-reports/snapshot-collections/${snapshotCollectionId}/retry-failed-results`,
        method: "POST",
      }),
    }),
    getSnapshotGridRows: builder.query<
      SnapshotGridQueryResponse,
      { snapshotCollectionId: string; payload: SnapshotGridQueryPayload }
    >({
      providesTags: (_result, _error, { snapshotCollectionId }) => collectionGridTags(snapshotCollectionId),
      query: ({ snapshotCollectionId, payload }) => ({
        url: `/pf-mainweb/business-reports/snapshot-collections/${snapshotCollectionId}/grid-query`,
        method: "POST",
        data: payload,
      }),
    }),
    getFamilySourceGridRows: builder.query<
      Pick<SnapshotGridQueryResponse, "results" | "total" | "offset" | "limit">,
      { snapshotCollectionId: string; familyOutcomeId: string; offset: number; limit: number }
    >({
      providesTags: (_result, _error, { snapshotCollectionId }) => collectionGridTags(snapshotCollectionId),
      query: ({ snapshotCollectionId, familyOutcomeId, offset, limit }) => ({
        url: `/pf-mainweb/business-reports/snapshot-collections/${snapshotCollectionId}/family-sources/${familyOutcomeId}/grid-query`,
        method: "POST",
        data: { offset, limit },
      }),
    }),
    getContractFamilyProjectionGridRows: builder.query<
      ContractFamilyProjectionGridQueryResponse,
      { snapshotCollectionId: string; payload: SnapshotGridQueryPayload }
    >({
      providesTags: (_result, _error, { snapshotCollectionId }) => collectionGridTags(snapshotCollectionId),
      query: ({ snapshotCollectionId, payload }) => ({
        url: `/pf-mainweb/document/contract-families/collections/${snapshotCollectionId}/grid-query`,
        method: "POST",
        data: payload,
      }),
    }),
    getSnapshotGridRowByResult: builder.query<
      BusinessReportOutcome,
      { snapshotCollectionId: string; resultId: string }
    >({
      providesTags: [BUSINESS_REPORTS_TAGS.BUSINESS_REPORTS_OUTCOME_TAG],
      query: ({ snapshotCollectionId, resultId }) => ({
        url: `/pf-mainweb/business-reports/snapshot-collections/${snapshotCollectionId}/grid-result/${resultId}`,
      }),
    }),
    getContractFamilyProjectionGridRowByResult: builder.query<
      BusinessReportOutcome,
      { snapshotCollectionId: string; resultId: string }
    >({
      providesTags: [BUSINESS_REPORTS_TAGS.BUSINESS_REPORTS_OUTCOME_TAG],
      query: ({ snapshotCollectionId, resultId }) => ({
        url: `/pf-mainweb/document/contract-families/collections/${snapshotCollectionId}/grid-results/${resultId}`,
      }),
    }),
    cancelSnapshotCollection: builder.mutation<void, { id: string }>({
      invalidatesTags: [
        BUSINESS_REPORTS_TAGS.BUSINESS_REPORTS_OUTCOME_TAG,
        SNAPSHOT_COLLECTION_TAGS.SNAPSHOT_COLLECTION_TAG,
      ],
      query: ({ id }) => ({
        url: `/pf-mainweb/business-reports/snapshot-collections/${id}/cancel`,
        method: "POST",
      }),
    }),
    // Cell edits never invalidate the bare outcome tag: `grid-query` is the heaviest call on the page and
    // the response (or the request body) already says what changed. The cached copies are patched in
    // place and the edited row alone is re-read; see `syncOutcomeCaches`.
    updateBusinessReportOutcomeResult: builder.mutation<
      BusinessReportOutcomeDetails,
      { id: string; resultId: string; data: { result?: string; rating?: TrafficLightRating | null } }
    >({
      // The body can only carry a value or a rating, never a name or data type, so the pivot field
      // schema (column names and types) is unaffected.
      query: ({ id, resultId, data }) => ({
        url: `/pf-mainweb/business-reports/business-report-outcome/${id}/update/${resultId}`,
        method: "POST",
        data,
      }),
      async onQueryStarted({ id, resultId, data }, api) {
        const patchResult = api.dispatch(
          gridApi.util.updateQueryData("getBusinessReportOutcome", id, draft => {
            Object.assign(draft, {
              result: draft.result.map(row => {
                if (row.id !== resultId) return row;
                const updates: Record<string, unknown> = {};
                // A date cell keeps its text too; the server parses the date and its response replaces this row.
                if (data.result !== undefined) {
                  updates.result = data.result;
                }
                if (data.rating !== undefined) {
                  updates.rating = data.rating;
                }
                return { ...row, ...updates };
              }),
            });
          }),
        );

        let updatedOutcome: BusinessReportOutcomeDetails;
        try {
          ({ data: updatedOutcome } = await api.queryFulfilled);
        } catch {
          patchResult.undo();
          return;
        }
        api.dispatch(
          gridApi.util.updateQueryData("getBusinessReportOutcome", id, draft => {
            Object.assign(draft, updatedOutcome);
          }),
        );
        await syncOutcomeCaches(
          {
            outcomeId: id,
            patch: outcomePatchFromDetails(updatedOutcome),
            resultId,
            snapshotCollectionId: updatedOutcome.snapshot_collection_id,
          },
          api,
        );
      },
    }),
    createBusinessReportOutcomeNote: builder.mutation<
      BusinessReportOutcomeDetails,
      { outcomeId: string; content: string; name?: string }
    >({
      query: ({ outcomeId, content, name }) => ({
        url: `/pf-mainweb/business-reports/business-report-outcome/${outcomeId}/notes`,
        method: "POST",
        data: { content, ...(name ? { name } : {}) },
      }),
      async onQueryStarted({ outcomeId }, api) {
        let updatedOutcome: BusinessReportOutcomeDetails;
        try {
          ({ data: updatedOutcome } = await api.queryFulfilled);
        } catch {
          return;
        }
        api.dispatch(
          gridApi.util.updateQueryData("getBusinessReportOutcome", outcomeId, draft => {
            Object.assign(draft, updatedOutcome);
          }),
        );
        await syncOutcomeCaches(
          {
            outcomeId,
            patch: outcomePatchFromDetails(updatedOutcome),
            snapshotCollectionId: updatedOutcome.snapshot_collection_id,
          },
          api,
        );
      },
    }),
    updateBusinessReportOutcome: builder.mutation<
      void,
      {
        id: string;
        data: Partial<
          {
            snapshot_collection_id?: string;
            snapshot_collection_new?: string;
          } & BusinessReportOutcome
        >;
      }
    >({
      // Moving an outcome between collections changes which grids contain it: that still needs the
      // full refetch. A verified toggle or a rename is patched in place instead.
      invalidatesTags: (_result, _error, { data }) =>
        isInPlaceOutcomePatch(data) ? [] : [BUSINESS_REPORTS_TAGS.BUSINESS_REPORTS_OUTCOME_TAG],
      query: ({ id, data }) => ({
        url: `/pf-mainweb/business-reports/business-report-outcome/${id}`,
        method: "PATCH",
        data,
      }),
      async onQueryStarted({ id, data }, api) {
        if (!isInPlaceOutcomePatch(data)) return;
        try {
          await api.queryFulfilled;
        } catch {
          return;
        }
        const patch: OutcomeCachePatch = { verified: data.verified, name: data.name };
        api.dispatch(
          gridApi.util.updateQueryData("getBusinessReportOutcome", id, draft => applyOutcomePatch(draft, patch)),
        );
        await syncOutcomeCaches({ outcomeId: id, patch }, api);
      },
    }),
    createNoteColumn: builder.mutation<
      { created: number },
      { snapshotCollectionId: string; businessReportId: string; name: string }
    >({
      invalidatesTags: [BUSINESS_REPORTS_TAGS.BUSINESS_REPORTS_OUTCOME_TAG],
      query: ({ snapshotCollectionId, businessReportId, name }) => ({
        url: `/pf-mainweb/business-reports/snapshot-collections/${snapshotCollectionId}/note-columns/${businessReportId}`,
        method: "POST",
        data: { name },
      }),
    }),
    createAiColumn: builder.mutation<
      { name: string; order: number },
      {
        snapshotCollectionId: string;
        businessReportId: string;
        name: string;
        prompt: string;
        full_context?: boolean;
        is_date?: boolean;
        data_type?: PromptDataType | null;
        use_image?: boolean;
        no_retriever?: boolean;
        web_search?: boolean;
        traffic_light?: boolean;
      }
    >({
      invalidatesTags: [
        BUSINESS_REPORTS_TAGS.BUSINESS_REPORTS_OUTCOME_TAG,
        BUSINESS_REPORTS_TAGS.BUSINESS_REPORTS_TAG,
        BUSINESS_REPORTS_TAGS.PIVOT_FIELD_SCHEMA_TAG,
      ],
      query: ({ snapshotCollectionId, businessReportId, ...data }) => ({
        url: `/pf-mainweb/business-reports/snapshot-collections/${snapshotCollectionId}/ai-columns/${businessReportId}`,
        method: "POST",
        data,
      }),
    }),
    rerunSnapshotCollectionColumn: builder.mutation<
      { name: string; rerun_cells: number },
      { snapshotCollectionId: string; businessReportPromptId: string }
    >({
      invalidatesTags: [BUSINESS_REPORTS_TAGS.BUSINESS_REPORTS_OUTCOME_TAG],
      query: ({ snapshotCollectionId, businessReportPromptId }) => ({
        url: `/pf-mainweb/business-reports/snapshot-collections/${snapshotCollectionId}/column-reruns`,
        method: "POST",
        data: { business_report_prompt_id: businessReportPromptId },
      }),
    }),
    getAiColumnProgress: builder.query<
      { pending: number; total: number },
      { snapshotCollectionId: string; name: string }
    >({
      query: ({ snapshotCollectionId, name }) => ({
        url: `/pf-mainweb/business-reports/snapshot-collections/${snapshotCollectionId}/ai-columns/progress`,
        method: "GET",
        params: { name },
      }),
    }),
    createSnapshotReviewRun: builder.mutation<
      SnapshotReviewRun,
      { snapshotCollectionId: string; businessReportPromptId: string; executionMode: "sync" | "batch" }
    >({
      invalidatesTags: [BUSINESS_REPORTS_TAGS.BUSINESS_REPORTS_OUTCOME_TAG],
      query: ({ snapshotCollectionId, businessReportPromptId, executionMode }) => ({
        url: `/pf-mainweb/business-reports/snapshot-collections/${snapshotCollectionId}/review-runs`,
        method: "POST",
        data: { business_report_prompt_id: businessReportPromptId, execution_mode: executionMode },
      }),
    }),
    getSnapshotReviewRun: builder.query<
      SnapshotReviewRunProgress,
      { snapshotCollectionId: string; businessReportPromptId: string }
    >({
      query: ({ snapshotCollectionId, businessReportPromptId }) => ({
        url: `/pf-mainweb/business-reports/snapshot-collections/${snapshotCollectionId}/review-runs`,
        method: "GET",
        params: { business_report_prompt_id: businessReportPromptId },
      }),
    }),
    renameNoteColumn: builder.mutation<
      { renamed: number },
      { snapshotCollectionId: string; businessReportId: string; oldName: string; newName: string }
    >({
      invalidatesTags: [BUSINESS_REPORTS_TAGS.BUSINESS_REPORTS_OUTCOME_TAG],
      query: ({ snapshotCollectionId, businessReportId, oldName, newName }) => ({
        url: `/pf-mainweb/business-reports/snapshot-collections/${snapshotCollectionId}/note-columns/${businessReportId}`,
        method: "PATCH",
        data: { old_name: oldName, new_name: newName },
      }),
    }),
    deleteNoteColumn: builder.mutation<
      { deleted: number },
      { snapshotCollectionId: string; businessReportId: string; columnName: string }
    >({
      invalidatesTags: [BUSINESS_REPORTS_TAGS.BUSINESS_REPORTS_OUTCOME_TAG],
      query: ({ snapshotCollectionId, businessReportId, columnName }) => ({
        url: `/pf-mainweb/business-reports/snapshot-collections/${snapshotCollectionId}/note-columns/${businessReportId}/${encodeURIComponent(columnName)}`,
        method: "DELETE",
      }),
    }),
  }),
});

type CacheLifecycleApi = Pick<
  Parameters<NonNullable<TypedMutationOnQueryStarted<unknown, unknown, BaseQueryFn, "api">>>[1],
  "dispatch" | "getState"
>;

const GRID_PAGE_ENDPOINTS = ["getSnapshotGridRows", "getContractFamilyProjectionGridRows"] as const;

interface OutcomeCacheSync {
  outcomeId: string;
  patch: OutcomeCachePatch;
  /** The edited result; when absent (a verified toggle) any result of the cached row identifies it. */
  resultId?: string;
  snapshotCollectionId?: string | null;
}

/**
 * Applies a cell edit to every cached copy of the outcome, then brings the edited row back in sync with
 * the server through the single-row `grid-result` endpoint (the grid merges those rows itself, see
 * `useRefreshedSnapshotGridRows`). Collections whose grid is a contract-family projection are
 * invalidated as a whole instead: the family-summary rows derive a stale indicator from the source
 * cells, which only the page query recomputes.
 */
const syncOutcomeCaches = async (
  { outcomeId, patch, resultId, snapshotCollectionId }: OutcomeCacheSync,
  { dispatch, getState }: CacheLifecycleApi,
): Promise<void> => {
  const state = getState();
  const collectionIds = new Set<string>(snapshotCollectionId ? [snapshotCollectionId] : []);
  const collectionsNeedingPageRefetch = new Set<string>();
  let rowResultId = resultId;

  for (const endpointName of GRID_PAGE_ENDPOINTS) {
    for (const args of gridApi.util.selectCachedArgsForQuery(state, endpointName)) {
      dispatch(
        gridApi.util.updateQueryData(endpointName, args, draft => {
          const row = draft.results.find(result => result.id === outcomeId);
          if (!row) return;
          collectionIds.add(args.snapshotCollectionId);
          rowResultId ??= storedResultId(row);
          if (patchAddsUnknownNoteColumn(draft.fields, patch)) {
            collectionsNeedingPageRefetch.add(args.snapshotCollectionId);
          }
          applyOutcomePatch(row, patch);
        }),
      );
    }
  }
  for (const args of gridApi.util.selectCachedArgsForQuery(state, "getFamilySourceGridRows")) {
    dispatch(
      gridApi.util.updateQueryData("getFamilySourceGridRows", args, draft => {
        patchOutcomeInRows(draft.results, outcomeId, patch);
      }),
    );
  }
  for (const args of snapshotViewsApi.util.selectCachedArgsForQuery(state, "getSnapshotViewGridRows")) {
    dispatch(
      snapshotViewsApi.util.updateQueryData("getSnapshotViewGridRows", args, draft => {
        patchOutcomeInRows(draft.results, outcomeId, patch);
      }),
    );
  }
  for (const args of gridApi.util.selectCachedArgsForQuery(state, "getBusinessReportsOutcome")) {
    dispatch(
      gridApi.util.updateQueryData("getBusinessReportsOutcome", args, draft => {
        patchOutcomeInRows(draft.results, outcomeId, patch);
      }),
    );
  }

  const projectionCollectionIds = new Set(
    gridApi.util
      .selectCachedArgsForQuery(state, "getContractFamilyProjectionGridRows")
      .map(args => args.snapshotCollectionId),
  );
  const pageRefetchTags: Array<{ type: string; id: string }> = [];
  for (const collectionId of collectionIds) {
    if (projectionCollectionIds.has(collectionId) || collectionsNeedingPageRefetch.has(collectionId)) {
      pageRefetchTags.push({ type: BUSINESS_REPORTS_TAGS.BUSINESS_REPORTS_OUTCOME_TAG, id: collectionId });
      continue;
    }
    if (!rowResultId) continue;
    const rowFetch = dispatch(
      gridApi.endpoints.getSnapshotGridRowByResult.initiate(
        { snapshotCollectionId: collectionId, resultId: rowResultId },
        { forceRefetch: true },
      ),
    );
    try {
      const row = await rowFetch.unwrap();
      for (const endpointName of GRID_PAGE_ENDPOINTS) {
        for (const args of gridApi.util.selectCachedArgsForQuery(getState(), endpointName)) {
          if (args.snapshotCollectionId !== collectionId) continue;
          dispatch(
            gridApi.util.updateQueryData(endpointName, args, draft => {
              replaceOutcomeInRows(draft.results, row);
            }),
          );
        }
      }
    } catch {
      // The patched copy stays; the next page fetch reconciles it.
    } finally {
      rowFetch.unsubscribe();
    }
  }
  if (pageRefetchTags.length > 0) {
    dispatch(gridApi.util.invalidateTags(pageRefetchTags));
  }
};

export const {
  useCancelSnapshotCollectionMutation,
  useCreateBusinessReportOutcomeNoteMutation,
  useCreateNoteColumnMutation,
  useRenameNoteColumnMutation,
  useDeleteNoteColumnMutation,
  useRetrySnapshotCollectionFailedResultsMutation,
  useGetSnapshotGridRowsQuery,
  useGetFamilySourceGridRowsQuery,
  useLazyGetSnapshotGridRowByResultQuery,
  useGetContractFamilyProjectionGridRowsQuery,
  useLazyGetContractFamilyProjectionGridRowByResultQuery,
  useCreateAiColumnMutation,
  useRerunSnapshotCollectionColumnMutation,
  useLazyGetAiColumnProgressQuery,
  useCreateSnapshotReviewRunMutation,
  useLazyGetSnapshotReviewRunQuery,
  useUpdateBusinessReportOutcomeMutation,
  useUpdateBusinessReportOutcomeResultMutation,
} = gridApi;
